import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, InputLabel, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from '~/assets/icons';
import BaseRow from '~/components/common/base-row/BaseRow';
import { cast, handleApiError, notify } from '~/utils/helper';
import { IEditTechnician } from '~/utils/interface/account';
import userValidation from '~/validation/user';
import utilityApi from '~/apis/utility';
import { IMasterDataItem } from '~/utils/interface/master';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import accountApi from '~/apis/account';
import { IUser } from '~/utils/interface/user';
import { useAppDispatch } from '~/redux/hooks';
import { accountActions } from '~/thunks/account/accountSlice';

type AssignToTechnicianDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  data?: IUser;
  setRefreshData?: (open: boolean) => void;
};

const initialValues: IEditTechnician = {
  technician: '',
};

const AssignToTechnicianDialog = (props: AssignToTechnicianDialogProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('admin/user');

  const [technicianOptions, setTechnicianOptions] = useState<Array<IMasterDataItem>>([]);

  useEffect(() => {
    if (props.open) {
      (async () => {
        try {
          const response = await utilityApi.getListTechnicians();
          setTechnicianOptions(
            response.data.map((item) => {
              return {
                id: item.username,
                name: item.username + ' - ' + item.name,
                data0: item.name,
              };
            })
          );
        } catch (error) {
          handleApiError(error);
        }
      })();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.data?.username]);

  const form = useFormik({
    validateOnChange: false,
    validationSchema: userValidation(t).assignToTechnician,
    initialValues,
    onSubmit: async (values) => {
      try {
        values.username = props.data?.username;
        const response = await accountApi.assignToTechnician(values);
        notify('success', (response?.data as any).message);
        props.setRefreshData?.(true);
        dispatch(accountActions.setTechnician(cast(values.technician)));
        handleSetOpen(false, false);
      } catch (e: any) {
        handleApiError(e, true);
      }
    },
  });

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  return (
    <Box className='w-full'>
      <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle className='bg-[#005F73]'>
          {t('assignTechnician')}
          <IconButton onClick={() => handleSetOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box component='form'>
            <Box className='w-full'>
              <BaseRow className='gap-5 mb-5 !items-center'>
                <Box className='w-1/2'>
                  <Typography className='font-bold'>{t('username')}:</Typography>
                </Box>
                <Box className='w-full text-right'>
                  <Typography className='font-bold'>{props.data?.username}</Typography>
                </Box>
              </BaseRow>
              <BaseRow className='gap-5 mb-5 !items-center'>
                <Box className='w-1/2'>
                  <InputLabel required={true}>{t('technician')}:</InputLabel>
                </Box>
                <Box className='w-full'>
                  <BaseSelect
                    size='medium'
                    options={technicianOptions}
                    value={form.values.technician}
                    placeholder={t('select')}
                    name='technician'
                    isSearchEnable
                    onChange={form.handleChange}
                    error={Boolean(form.errors?.technician)}
                    helperText={form.errors?.technician}
                  />
                </Box>
              </BaseRow>
            </Box>

            <Button
              variant='contained'
              color='secondary'
              className='min-w-[125px] flex ml-auto mt-[29px]'
              onClick={() => form.handleSubmit()}
            >
              {t('add')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default memo(AssignToTechnicianDialog);
